/**
 * B2B Set Quantity Handler
 * Enforces setofX multiples on product page and AJAX cart
 * Compatible with Hospitality Provisions B2B Apollo theme
 *
 * Works with two contexts:
 *  1. Product page  – .quantity-control-up/down buttons + .js-qty-input
 *  2. AJAX cart     – [data-ajax-qty-increase/decrease] buttons + .ajax-cart__qty-input
 *
 * Does NOT touch:
 *  - Approved Customer / price-visibility logic (webpanda_hide_price)
 *  - Sales_Rep conditional sections
 *  - PDF Quote app (Vify Order Printer)
 *  - Locksmith access-control logic
 */

(function () {
  'use strict';

  /* ─── Helpers ─────────────────────────────────────────────── */

  /**
   * Parse setofX from a comma-separated or array of product tags.
   * Supports both "setof6" and "[setof6]" formats.
   * Returns the set size (integer ≥ 2), or 1 if not found.
   */
  function parseSetQty(tags) {
    if (!tags) return 1;
    var list = Array.isArray(tags) ? tags : String(tags).split(',');
    for (var i = 0; i < list.length; i++) {
      var t = list[i].toLowerCase().replace(/[\[\]\s]/g, '');
      var m = t.match(/^setof(\d+)$/);
      if (m) {
        var n = parseInt(m[1], 10);
        if (n >= 2) return n;
      }
    }
    return 1;
  }

  /**
   * Snap a value to the nearest ≥ 1 multiple of setQty.
   * Never goes below setQty when value > 0.
   */
  function snap(value, setQty) {
    if (setQty <= 1) return value;
    if (value <= 0) return 0;
    var rounded = Math.round(value / setQty) * setQty;
    return rounded < setQty ? setQty : rounded;
  }

  /**
   * Clone a DOM node to strip all existing event listeners,
   * then replace it in the tree. Returns the clone.
   */
  function stripListeners(el) {
    var clone = el.cloneNode(true);
    el.parentNode.replaceChild(clone, el);
    return clone;
  }

  /**
   * Show a temporary inline notice next to an element.
   */
  function showNotice(anchorEl, setQty, adjQty) {
    var existing = anchorEl.parentNode.querySelector('.setqty-notice');
    if (existing) existing.remove();

    var n = document.createElement('p');
    n.className = 'setqty-notice';
    n.style.cssText = 'color:#c0392b;font-size:12px;margin:4px 0 0;';
    n.textContent = 'This item is sold in sets of ' + setQty + '. Quantity adjusted to ' + adjQty + '.';
    anchorEl.parentNode.insertBefore(n, anchorEl.nextSibling);

    setTimeout(function () { n.remove(); }, 3500);
  }


  /* ─── 1. PRODUCT PAGE ─────────────────────────────────────── */

  function initProductPage() {
    // The wrapper div has data-set-quantity injected by product-quantity.liquid
    var wrapper = document.querySelector('.qty-selection[data-set-quantity]');
    if (!wrapper) return;

    var setQty = parseInt(wrapper.dataset.setQuantity, 10) || 1;
    if (setQty < 2) return; // Not a set product, leave theme alone

    var qtyInput  = wrapper.querySelector('.js-qty-input');
    var formInput = wrapper.querySelector('.formQty') ||
                    document.querySelector('.formQty');  // formQty is outside the snippet
    var upBtn     = wrapper.querySelector('.quantity-control-up');
    var downBtn   = wrapper.querySelector('.quantity-control-down');

    if (!qtyInput || !upBtn || !downBtn) return;

    // Set initial value & min
    qtyInput.setAttribute('min', setQty);
    qtyInput.value = setQty;
    if (formInput) formInput.value = setQty;

    // Replace buttons to strip theme.js listeners
    upBtn   = stripListeners(upBtn);
    downBtn = stripListeners(downBtn);

    upBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      var cur = parseInt(qtyInput.value, 10) || setQty;
      qtyInput.value = cur + setQty;
      syncForm();
    });

    downBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      var cur = parseInt(qtyInput.value, 10) || setQty;
      var next = cur - setQty;
      qtyInput.value = next < setQty ? setQty : next;
      syncForm();
    });

    // Validate manual entry on blur
    qtyInput.addEventListener('blur', function () {
      var v   = parseInt(qtyInput.value, 10);
      if (isNaN(v) || v <= 0) { qtyInput.value = setQty; }
      else {
        var snapped = snap(v, setQty);
        if (snapped !== v) {
          qtyInput.value = snapped;
          showNotice(qtyInput, setQty, snapped);
        }
      }
      syncForm();
    });

    function syncForm() {
      if (formInput) formInput.value = qtyInput.value;
      // Trigger change so theme keeps its internal state consistent
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }


  /* ─── 2. AJAX CART PAGE ───────────────────────────────────── */

  /**
   * Read data-set-quantity from a cart qty input element.
   * Returns integer ≥ 2 for set products, or 1 if not a set product.
   */
  function getSetQtyFromInput(input) {
    if (!input || !input.dataset || !input.dataset.setQuantity) return 1;
    var n = parseInt(input.dataset.setQuantity, 10);
    return (n >= 2) ? n : 1;
  }

  /**
   * Snap the value of a cart qty input in-place.
   * Returns true if the value was changed.
   * Skips snapping for quantity 0 (removal).
   */
  function snapCartInput(input) {
    var setQty = getSetQtyFromInput(input);
    if (setQty < 2) return false;

    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 0) { input.value = setQty; return true; }
    if (v === 0) return false; // allow removal

    var snapped = snap(v, setQty);
    if (snapped !== v) {
      input.value = snapped;
      showNotice(input, setQty, snapped);
      return true;
    }
    return false;
  }

  /**
   * Snap ALL cart qty inputs on the page.
   * Called as a safety net before form submission / checkout.
   */
  function snapAllCartInputs() {
    document.querySelectorAll('.ajax-cart__qty-input[data-set-quantity]').forEach(function (input) {
      snapCartInput(input);
    });
  }

  function initCart() {
    if (!window.b2bCartItemsData) return;

    /* --- 2a. Patch Shopify.theme.ajaxCart.adjustQty --- */
    patchAdjustQty();

    /* --- 2b. Patch Shopify.theme.cart.updateItem as safety net --- */
    patchUpdateItem();

    /* --- 2c. Event delegation on the cart form (survives AJAX re-renders) --- */
    var cartForm = document.querySelector('.js-cart-form');
    if (!cartForm) cartForm = document.querySelector('form[action*="/cart"]');
    if (!cartForm) return;

    /*
     * CAPTURE-phase listeners on the form catch events from dynamically
     * replaced input elements without needing to rebind after each AJAX
     * cart refresh. We intercept BEFORE the theme's bubble-phase handler.
     */

    // Snap on 'change' (fires on blur from a typed value, or programmatic dispatch)
    cartForm.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.classList.contains('ajax-cart__qty-input')) return;
      if (getSetQtyFromInput(input) < 2) return;

      // Flag to prevent infinite loop from re-dispatched change events
      if (input._setQtySnapping) return;

      var changed = snapCartInput(input);
      if (changed) {
        // Stop this event so the theme handler doesn't see the bad value.
        // Then dispatch a fresh change with the corrected value.
        e.stopImmediatePropagation();
        input._setQtySnapping = true;
        setTimeout(function () {
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input._setQtySnapping = false;
        }, 0);
      }
    }, true); // capture phase

    // Snap on 'blur' (covers tabbing away from the field)
    cartForm.addEventListener('blur', function (e) {
      var input = e.target;
      if (!input.classList.contains('ajax-cart__qty-input')) return;
      snapCartInput(input);
    }, true);

    // Snap on Enter key BEFORE the form can submit
    cartForm.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var input = e.target;
      if (!input.classList.contains('ajax-cart__qty-input')) return;
      if (getSetQtyFromInput(input) < 2) return;

      snapCartInput(input);
    }, true);

    // Intercept native form submission to snap all inputs first
    cartForm.addEventListener('submit', function () {
      snapAllCartInputs();
    }, true);

    /* --- 2d. Set step attribute on existing inputs for scroll/arrow keys --- */
    applyStepAttributes();

    /* --- 2e. Re-apply step attributes after AJAX cart re-render --- */
    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function () {
        applyStepAttributes();
      });
      observer.observe(cartForm, { childList: true, subtree: true });
    }
  }

  /**
   * Set the HTML step/min attributes on cart qty inputs so that
   * browser arrow keys and scroll also move in set increments.
   */
  function applyStepAttributes() {
    document.querySelectorAll('.ajax-cart__qty-input[data-set-quantity]').forEach(function (input) {
      var setQty = getSetQtyFromInput(input);
      if (setQty < 2) return;
      input.setAttribute('step', setQty);
      input.setAttribute('min', setQty);
      // If the current value is invalid, snap it now
      var v = parseInt(input.value, 10);
      if (!isNaN(v) && v > 0 && v % setQty !== 0) {
        input.value = snap(v, setQty);
      }
    });
  }

  /**
   * Monkey-patch Shopify.theme.ajaxCart.adjustQty once it exists.
   * The patch reads data-set-quantity from the input and uses it
   * as the step size instead of ±1.
   * Guard flag prevents double-patching on cart refresh.
   */
  function patchAdjustQty() {
    var waitCount = 0;
    var interval = setInterval(function () {
      waitCount++;
      if (waitCount > 80) { clearInterval(interval); return; } // give up after ~8s

      var ajaxCart = window.Shopify &&
                     window.Shopify.theme &&
                     window.Shopify.theme.ajaxCart;
      if (!ajaxCart || !ajaxCart.adjustQty) return;
      if (ajaxCart._setQtyPatched) { clearInterval(interval); return; }

      var original = ajaxCart.adjustQty.bind(ajaxCart);
      ajaxCart.adjustQty = function (value, itemId, config) {
        var input = document.querySelector('.updates_' + itemId);
        if (input) {
          var setQty = parseInt(input.dataset.setQuantity, 10) || 1;
          if (setQty >= 2) {
            // value is ±1 from theme; multiply to get setQty step
            value = value > 0 ? setQty : -setQty;
          }
        }
        return original(value, itemId, config);
      };

      ajaxCart._setQtyPatched = true;
      clearInterval(interval);
    }, 100);
  }

  /**
   * Monkey-patch Shopify.theme.cart.updateItem as a final safety net.
   * If any code path sends a non-multiple quantity to the cart API,
   * this patch snaps it before the fetch goes out.
   */
  function patchUpdateItem() {
    var waitCount = 0;
    var interval = setInterval(function () {
      waitCount++;
      if (waitCount > 80) { clearInterval(interval); return; }

      var cart = window.Shopify &&
                 window.Shopify.theme &&
                 window.Shopify.theme.cart;
      if (!cart || !cart.updateItem) return;
      if (cart._setQtyUpdatePatched) { clearInterval(interval); return; }

      var originalUpdate = cart.updateItem.bind(cart);
      cart.updateItem = function (key, options) {
        if (options && typeof options.quantity === 'number' && options.quantity > 0) {
          // Find the input by item key to read its set quantity
          var input = document.querySelector('.ajax-cart__qty-input[data-item-key="' + key + '"]');
          if (input) {
            var setQty = getSetQtyFromInput(input);
            if (setQty >= 2) {
              options.quantity = snap(options.quantity, setQty);
            }
          }
        }
        return originalUpdate(key, options);
      };

      cart._setQtyUpdatePatched = true;
      clearInterval(interval);
    }, 100);
  }


  /* ─── Bootstrap ───────────────────────────────────────────── */

  function boot() {
    // Product page (static DOM)
    if (document.querySelector('.qty-selection')) {
      initProductPage();
    }

    // Cart page (AJAX – b2bCartItemsData injected by cart-form.liquid)
    if (window.b2bCartItemsData) {
      initCart();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();